import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getProjects, createProject, updateProject, deleteProject,
  addLineItem, updateLineItem, deleteLineItem, recordProjectSpend,
  updateSpendEntry, deleteSpendEntry,
  formatCurrency, handleNumericInput, parseNumericValue
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { DatePicker } from "../components/DatePicker";
import {
  Plus, Pencil, Trash2, FolderKanban, ArrowLeft, IndianRupee, TrendingUp,
  ChevronRight, AlertCircle, CheckCircle, Repeat, Download
} from "lucide-react";
import { downloadFile } from "../lib/download";

const COMPANIES = [
  { value: "empact-studio", label: "Empact Studio" },
  { value: "event-resource", label: "Event Resource" },
  { value: "vijay-sabhlok", label: "Vijay Sabhlok" },
];
const getCompanyLabel = (v) => COMPANIES.find(c => c.value === v)?.label || v;

export default function Projects() {
  const { canEdit } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [lineItemDialog, setLineItemDialog] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [spendDialog, setSpendDialog] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [form, setForm] = useState({ name: "", company: "empact-studio", description: "", estimated_budget: "", start_date: "", end_date: "" });
  const [liForm, setLiForm] = useState({ name: "", budgeted_amount: "", spent_amount: "", notes: "", type: "one-time" });
  const [spendForm, setSpendForm] = useState({ amount: "", description: "", date: "" });
  const [editingSpend, setEditingSpend] = useState(null); // { lineItemId, index, entry }

  const selectedProjectRef = React.useRef(selectedProject);
  selectedProjectRef.current = selectedProject;

  const fetchProjects = useCallback(async () => {
    try {
      const res = await getProjects();
      setProjects(res.data);
      if (selectedProjectRef.current) {
        const updated = res.data.find(p => p.id === selectedProjectRef.current.id);
        if (updated) setSelectedProject(updated);
      }
    } catch { toast.error("Failed to load projects"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const openCreateDialog = () => {
    setEditingProject(null);
    setForm({ name: "", company: "empact-studio", description: "", estimated_budget: "", start_date: "", end_date: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (p) => {
    setEditingProject(p);
    setForm({ name: p.name, company: p.company, description: p.description || "", estimated_budget: p.estimated_budget?.toString() || "", start_date: p.start_date || "", end_date: p.end_date || "" });
    setDialogOpen(true);
  };

  const handleSaveProject = async () => {
    if (!form.name || !form.estimated_budget) { toast.error("Name and budget required"); return; }
    try {
      const payload = { ...form, estimated_budget: parseNumericValue(form.estimated_budget) };
      if (editingProject) {
        await updateProject(editingProject.id, payload);
        toast.success("Project updated");
      } else {
        await createProject(payload);
        toast.success("Project created");
      }
      setDialogOpen(false);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteProject = async () => {
    try {
      await deleteProject(editingProject.id);
      toast.success("Project deleted");
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const handleSaveLineItem = async () => {
    if (!liForm.name || !liForm.budgeted_amount) { toast.error("Name and amount required"); return; }
    try {
      const payload = { name: liForm.name, budgeted_amount: parseNumericValue(liForm.budgeted_amount), notes: liForm.notes, type: liForm.type };
      if (editingLineItem && liForm.spent_amount !== "") {
        payload.spent_amount = parseNumericValue(liForm.spent_amount);
      }
      let res;
      if (editingLineItem) {
        res = await updateLineItem(selectedProject.id, editingLineItem.id, payload);
        toast.success("Line item updated");
      } else {
        res = await addLineItem(selectedProject.id, payload);
        toast.success("Line item added");
      }
      setSelectedProject(res.data);
      setLineItemDialog(false);
      setEditingLineItem(null);
      setLiForm({ name: "", budgeted_amount: "", spent_amount: "", notes: "", type: "one-time" });
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openEditLineItem = (li) => {
    setEditingLineItem(li);
    setLiForm({ name: li.name, budgeted_amount: String(li.budgeted_amount), spent_amount: String(li.spent_amount || 0), notes: li.notes || "", type: li.type || "one-time" });
    setLineItemDialog(true);
  };

  const handleExportProjects = async (format = "pdf") => {
    try {
      const url = `/api/projects/export/download?format=${format}&company=all`;
      const filename = `projects_${new Date().toISOString().split('T')[0]}.${format}`;
      await downloadFile(url, filename);
      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch { toast.error("Export failed"); }
  };

  const handleDeleteLineItem = async (itemId) => {
    if (!window.confirm("Remove this line item?")) return;
    try {
      const res = await deleteLineItem(selectedProject.id, itemId);
      setSelectedProject(res.data);
      toast.success("Line item removed");
      fetchProjects();
    } catch (err) { toast.error("Failed"); }
  };

  const handleRecordSpend = async () => {
    if (!spendForm.amount) { toast.error("Amount required"); return; }
    try {
      const res = await recordProjectSpend(selectedProject.id, {
        line_item_id: selectedLineItem.id,
        amount: parseNumericValue(spendForm.amount),
        description: spendForm.description,
        date: spendForm.date,
      });
      setSelectedProject(res.data);
      setSpendDialog(false);
      setSpendForm({ amount: "", description: "", date: "" });
      toast.success("Spend recorded");
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEditSpend = (lineItemId, index, entry) => {
    setEditingSpend({ lineItemId, index });
    setSpendForm({ amount: String(entry.amount), description: entry.description || "", date: entry.date || "" });
    setSpendDialog(true);
  };

  const handleUpdateSpend = async () => {
    if (!spendForm.amount) { toast.error("Amount required"); return; }
    try {
      const res = await updateSpendEntry(selectedProject.id, editingSpend.lineItemId, editingSpend.index, {
        amount: parseNumericValue(spendForm.amount),
        description: spendForm.description,
        date: spendForm.date,
      });
      setSelectedProject(res.data);
      setSpendDialog(false);
      setEditingSpend(null);
      setSpendForm({ amount: "", description: "", date: "" });
      toast.success("Spend entry updated");
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteSpend = async (lineItemId, index) => {
    if (!window.confirm("Delete this spend entry?")) return;
    try {
      const res = await deleteSpendEntry(selectedProject.id, lineItemId, index);
      setSelectedProject(res.data);
      toast.success("Spend entry removed");
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const progressColor = (pct) => pct > 100 ? "bg-red-500" : pct > 75 ? "bg-amber-500" : "bg-emerald-500";

  // --- Detail View ---
  if (selectedProject) {
    const p = selectedProject;
    const pct = p.total_budgeted > 0 ? (p.total_spent / p.total_budgeted) * 100 : 0;
    return (
      <div className="space-y-6 animate-fade-in" data-testid="project-detail">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSelectedProject(null)} data-testid="back-to-projects">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="page-title">{p.name}</h1>
              <p className="text-xs sm:text-sm text-slate-500">{getCompanyLabel(p.company)} {p.description && `· ${p.description}`}</p>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openEditDialog(p)} data-testid="edit-project-btn"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
              <Button variant="outline" size="sm" className="text-red-500" onClick={() => { setEditingProject(p); setDeleteDialogOpen(true); }} data-testid="delete-project-btn"><Trash2 className="w-4 h-4" /></Button>
            </div>
          )}
        </div>

        {/* Budget Summary */}
        <Card className="bg-brand-navy text-white">
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4 text-center">
              <div>
                <p className="text-white/70 text-[10px] sm:text-xs">Budget</p>
                <p className="text-sm sm:text-lg md:text-xl font-bold tabular-nums">{formatCurrency(p.total_budgeted)}</p>
              </div>
              <div>
                <p className="text-white/70 text-[10px] sm:text-xs">Allocated</p>
                <p className="text-sm sm:text-lg md:text-xl font-bold tabular-nums text-blue-300">{formatCurrency(p.total_allocated || 0)}</p>
              </div>
              <div>
                <p className="text-white/70 text-[10px] sm:text-xs">Spent</p>
                <p className={`text-sm sm:text-lg md:text-xl font-bold tabular-nums ${pct > 100 ? 'text-red-400' : 'text-brand-orange'}`}>{formatCurrency(p.total_spent)}</p>
              </div>
              <div>
                <p className="text-white/70 text-[10px] sm:text-xs">Variance</p>
                <p className={`text-sm sm:text-lg md:text-xl font-bold tabular-nums ${(p.variance || 0) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{formatCurrency(p.variance || 0)}</p>
                <p className={`text-[9px] sm:text-[10px] mt-0.5 ${(p.variance || 0) < 0 ? 'text-red-300' : 'text-emerald-300'}`}>
                  {(p.variance || 0) < 0 ? 'Cost Overrun' : 'Under Budget'}
                </p>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className="text-white/70 text-[10px] sm:text-xs">Remaining to Pay</p>
                <p className={`text-sm sm:text-lg md:text-xl font-bold tabular-nums ${(p.remaining_to_pay || 0) > 0 ? 'text-amber-300' : 'text-emerald-400'}`}>{formatCurrency(p.remaining_to_pay || 0)}</p>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-white/60 mb-1">
                <span>Spent vs Budget</span><span>{pct.toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${progressColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold">Line Items</CardTitle>
            {canEdit && (
              <Button size="sm" onClick={() => { setLiForm({ name: "", budgeted_amount: "", notes: "", type: "one-time" }); setLineItemDialog(true); }} className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="add-line-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {p.line_items?.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No line items yet. Add budget line items to track spending.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Allocated</TableHead>
                      <TableHead className="text-right">Spent</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead className="text-right">%</TableHead>
                      {canEdit && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {p.line_items.map((li) => {
                      const effectiveBudget = li.effective_budget || li.budgeted_amount;
                      const liPct = effectiveBudget > 0 ? (li.spent_amount / effectiveBudget) * 100 : 0;
                      const remaining = effectiveBudget - li.spent_amount;
                      return (
                        <TableRow key={li.id}>
                          <TableCell>
                            <p className="font-medium text-sm">{li.name}</p>
                            {li.notes && <p className="text-xs text-slate-400">{li.notes}</p>}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${li.type === 'recurring' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                              {li.type === 'recurring' ? <Repeat className="w-3 h-3 mr-1" /> : null}
                              {li.type === 'recurring' ? 'Monthly' : 'One-time'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {formatCurrency(effectiveBudget)}
                            {li.type === 'recurring' && <p className="text-[10px] text-slate-400">{formatCurrency(li.budgeted_amount)}/mo x {p.project_months || '?'}mo</p>}
                          </TableCell>
                          <TableCell className={`text-right tabular-nums text-sm font-medium ${liPct > 100 ? 'text-red-600' : ''}`}>{formatCurrency(li.spent_amount)}</TableCell>
                          <TableCell className={`text-right tabular-nums text-sm ${remaining < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(remaining)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${progressColor(liPct)}`} style={{ width: `${Math.min(liPct, 100)}%` }} />
                              </div>
                              <span className="text-xs text-slate-500 w-10 text-right">{liPct.toFixed(0)}%</span>
                            </div>
                          </TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-brand-navy" onClick={() => { setSelectedLineItem(li); setSpendForm({ amount: "", description: "", date: "" }); setSpendDialog(true); }} data-testid={`record-spend-${li.id}`}>
                                  <IndianRupee className="w-3 h-3 mr-1" /> Spend
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={() => openEditLineItem(li)} data-testid={`edit-li-${li.id}`}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteLineItem(li.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Spend History */}
        {p.line_items?.some(li => li.spend_history?.length > 0) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Spend Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {p.line_items.flatMap(li => (li.spend_history || []).map((s, idx) => ({ ...s, line_item: li.name, line_item_id: li.id, spend_index: idx })))
                  .sort((a, b) => (b.recorded_at || "").localeCompare(a.recorded_at || ""))
                  .slice(0, 30)
                  .map((s, i) => (
                    <div key={`${s.line_item_id}-${s.spend_index}`} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm group">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">{s.line_item}</p>
                        <p className="text-xs text-slate-500 truncate">{s.description || "No description"} · {s.date}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <p className="font-bold text-brand-navy tabular-nums">{formatCurrency(s.amount)}</p>
                        {canEdit && (
                          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-blue-500" onClick={() => handleEditSpend(s.line_item_id, s.spend_index, s)} data-testid={`edit-spend-${s.line_item_id}-${s.spend_index}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDeleteSpend(s.line_item_id, s.spend_index)} data-testid={`delete-spend-${s.line_item_id}-${s.spend_index}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                }
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Line Item Dialog */}
        <Dialog open={lineItemDialog} onOpenChange={(open) => { setLineItemDialog(open); if (!open) setEditingLineItem(null); }}>
          <DialogContent className="max-w-md" data-testid="line-item-dialog">
            <DialogHeader><DialogTitle>{editingLineItem ? "Edit Line Item" : "Add Line Item"}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div><Label>Item Name</Label><Input value={liForm.name} onChange={(e) => setLiForm({ ...liForm, name: e.target.value })} placeholder="e.g., Camera Equipment" data-testid="li-name" /></div>
              <div><Label>{liForm.type === "recurring" ? "Monthly Amount" : "Budgeted Amount"}</Label><Input value={liForm.budgeted_amount} onChange={(e) => setLiForm({ ...liForm, budgeted_amount: handleNumericInput(e.target.value) })} placeholder="0" data-testid="li-budget" /></div>
              <div><Label>Type</Label>
                <Select value={liForm.type} onValueChange={(v) => setLiForm({ ...liForm, type: v })}>
                  <SelectTrigger data-testid="li-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-time</SelectItem>
                    <SelectItem value="recurring">Recurring (Monthly)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {liForm.type === "recurring" && selectedProject && (
                <p className="text-xs text-slate-500">
                  Project duration: {selectedProject.project_months || "?"} months. 
                  Effective budget: {formatCurrency(parseNumericValue(liForm.budgeted_amount || "0") * (selectedProject.project_months || 1))}
                </p>
              )}
              {editingLineItem && (
                <div>
                  <Label>Spent Amount</Label>
                  <Input value={liForm.spent_amount} onChange={(e) => setLiForm({ ...liForm, spent_amount: handleNumericInput(e.target.value) })} placeholder="0" data-testid="li-spent" />
                  <p className="text-xs text-slate-400 mt-1">Override the total spent for this line item</p>
                </div>
              )}
              <div><Label>Notes (optional)</Label><Textarea value={liForm.notes} onChange={(e) => setLiForm({ ...liForm, notes: e.target.value })} placeholder="Any notes..." rows={2} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setLineItemDialog(false); setEditingLineItem(null); }}>Cancel</Button>
                <Button onClick={handleSaveLineItem} className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="save-line-item">{editingLineItem ? "Save Changes" : "Add Item"}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Record / Edit Spend Dialog */}
        <Dialog open={spendDialog} onOpenChange={(open) => { setSpendDialog(open); if (!open) { setEditingSpend(null); setSelectedLineItem(null); } }}>
          <DialogContent className="max-w-md" data-testid="spend-dialog">
            <DialogHeader><DialogTitle>{editingSpend ? "Edit Spend Entry" : `Record Spend — ${selectedLineItem?.name}`}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              {!editingSpend && selectedLineItem && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Budget</span><span className="font-medium">{formatCurrency(selectedLineItem?.budgeted_amount || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Spent so far</span><span className="font-medium">{formatCurrency(selectedLineItem?.spent_amount || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Remaining</span><span className="font-bold text-emerald-600">{formatCurrency((selectedLineItem?.budgeted_amount || 0) - (selectedLineItem?.spent_amount || 0))}</span></div>
                </div>
              )}
              <div><Label>Amount</Label><Input value={spendForm.amount} onChange={(e) => setSpendForm({ ...spendForm, amount: handleNumericInput(e.target.value) })} placeholder="0" data-testid="spend-amount" /></div>
              <div><Label>Description</Label><Input value={spendForm.description} onChange={(e) => setSpendForm({ ...spendForm, description: e.target.value })} placeholder="What was this for?" data-testid="spend-desc" /></div>
              <div><Label>Date</Label><DatePicker value={spendForm.date} onChange={(v) => setSpendForm({ ...spendForm, date: v })} data-testid="spend-date" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setSpendDialog(false); setEditingSpend(null); }}>Cancel</Button>
                <Button onClick={editingSpend ? handleUpdateSpend : handleRecordSpend} className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="confirm-spend">{editingSpend ? "Update Spend" : "Record Spend"}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Delete Project?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">This will permanently delete "{editingProject?.name}" and all its budget data.</p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDeleteProject} data-testid="confirm-delete-project">Delete</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Project Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="project-dialog">
            <DialogHeader><DialogTitle>Edit Project</DialogTitle></DialogHeader>
            <ProjectForm form={form} setForm={setForm} onSave={handleSaveProject} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // --- List View ---
  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">In-house project budgets and spend tracking</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleExportProjects("pdf")} data-testid="export-projects-btn">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            <Button onClick={openCreateDialog} className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-brand-orange border-t-transparent rounded-full" /></div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No projects yet</p>
            <p className="text-sm text-slate-500 mt-1">Create a project to start tracking budgets.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Bar */}
          <Card data-testid="projects-summary-bar">
            <CardContent className="py-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
                <div className="text-center">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Budget</p>
                  <p className="text-sm sm:text-lg md:text-xl font-bold text-brand-navy tabular-nums">{formatCurrency(projects.reduce((s, p) => s + (p.total_budgeted || 0), 0))}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Allocated</p>
                  <p className="text-sm sm:text-lg md:text-xl font-bold text-blue-600 tabular-nums">{formatCurrency(projects.reduce((s, p) => s + (p.total_allocated || 0), 0))}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Spent</p>
                  <p className="text-sm sm:text-lg md:text-xl font-bold text-brand-orange tabular-nums">{formatCurrency(projects.reduce((s, p) => s + (p.total_spent || 0), 0))}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Variance</p>
                  {(() => { const v = projects.reduce((s, p) => s + (p.variance || 0), 0); return (
                    <p className={`text-sm sm:text-lg md:text-xl font-bold tabular-nums ${v < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(v)}</p>
                  ); })()}
                </div>
                <div className="text-center col-span-2 sm:col-span-1">
                  <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Remaining to Pay</p>
                  {(() => { const r = projects.reduce((s, p) => s + (p.remaining_to_pay || 0), 0); return (
                    <p className={`text-sm sm:text-lg md:text-xl font-bold tabular-nums ${r > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{formatCurrency(r)}</p>
                  ); })()}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => {
            const pct = p.total_budgeted > 0 ? (p.total_spent / p.total_budgeted) * 100 : 0;
            return (
              <Card key={p.id} className="card-hover cursor-pointer" onClick={() => setSelectedProject(p)} data-testid={`project-card-${p.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <CardTitle className="text-base font-semibold truncate">{p.name}</CardTitle>
                      <p className="text-xs text-slate-500">{getCompanyLabel(p.company)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {pct > 100 ? <AlertCircle className="w-4 h-4 text-red-500" /> : pct === 100 ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : null}
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-center mb-3">
                    <div><p className="text-[10px] text-slate-400 uppercase">Budget</p><p className="text-sm font-bold tabular-nums">{formatCurrency(p.total_budgeted)}</p></div>
                    <div><p className="text-[10px] text-slate-400 uppercase">Allocated</p><p className="text-sm font-bold tabular-nums text-blue-600">{formatCurrency(p.total_allocated)}</p></div>
                    <div><p className="text-[10px] text-slate-400 uppercase">Spent</p><p className={`text-sm font-bold tabular-nums ${pct > 100 ? 'text-red-600' : ''}`}>{formatCurrency(p.total_spent)}</p></div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Variance</p>
                      <p className={`text-sm font-bold tabular-nums ${(p.variance || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {formatCurrency(p.variance || 0)}
                      </p>
                    </div>
                  </div>
                  {(p.variance || 0) < 0 && (
                    <div className="mb-2 px-2 py-1 bg-red-50 border border-red-200 rounded text-[10px] font-semibold text-red-600 text-center">
                      Cost Overrun: {formatCurrency(Math.abs(p.variance))}
                    </div>
                  )}
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${progressColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                  <p className="text-xs text-slate-400 mt-1 text-right">{pct.toFixed(1)}% spent · {p.line_items?.length || 0} items</p>
                </CardContent>
              </Card>
            );
          })}
          </div>
        </>
      )}

      {/* Create Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="project-dialog">
          <DialogHeader><DialogTitle>{editingProject ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <ProjectForm form={form} setForm={setForm} onSave={handleSaveProject} onCancel={() => setDialogOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectForm({ form, setForm, onSave, onCancel }) {
  return (
    <div className="space-y-4 pt-2">
      <div><Label>Project Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Brand Film 2026" data-testid="project-name" /></div>
      <div><Label>Company</Label>
        <Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
          <SelectTrigger data-testid="project-company"><SelectValue /></SelectTrigger>
          <SelectContent>
            {COMPANIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label>Estimated Total Budget</Label><Input value={form.estimated_budget} onChange={(e) => setForm({ ...form, estimated_budget: handleNumericInput(e.target.value) })} placeholder="0" data-testid="project-budget" /></div>
      <div><Label>Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Start Date</Label><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} data-testid="project-start" /></div>
        <div><Label>End Date</Label><DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} data-testid="project-end" /></div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onSave} className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="save-project">Save Project</Button>
      </div>
    </div>
  );
}
